import { memo } from "react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import type { HolidayInfo } from "@/types/proto/api/v1/memo_service_pb";
import { useTranslate } from "@/utils/i18n";
import { formatHolidayTradingHours, getHolidayType } from "@/utils/timezone";
import { DEFAULT_CELL_SIZE, SMALL_CELL_SIZE } from "./constants";
import type { CalendarDayCell, CalendarSize } from "./types";
import { getCellIntensityClass } from "./utils";

export interface CalendarCellProps {
  day: CalendarDayCell;
  maxCount: number;
  tooltipText: string;
  onClick?: (date: string) => void;
  size?: CalendarSize;
  holiday?: HolidayInfo;
}

export const CalendarCell = memo((props: CalendarCellProps) => {
  const { day, maxCount, tooltipText, onClick, size = "default", holiday } = props;
  const t = useTranslate();

  const handleClick = () => {
    if (day.count > 0 && onClick) {
      onClick(day.date);
    }
  };

  // Determine holiday type for icon styling
  const holidayType = holiday ? getHolidayType(holiday) : null;

  // Get holiday icon based on type
  const getHolidayIcon = () => {
    if (!holiday) return null;

    const iconClasses = cn(
      "absolute rounded-full ring-2 ring-background",
      size === "small" ? "w-1.5 h-1.5 -top-0.5 -right-0.5" : "w-2 h-2 -top-0.5 -right-0.5",
    );

    switch (holidayType) {
      case "full-closed":
        return <div className={cn(iconClasses, "bg-red-500")} title={`${holiday.name} - Market Closed`} />;
      case "early-close":
        return (
          <div className={cn(iconClasses, "bg-yellow-500")} title={`${holiday.name} - Early Close`}>
            <span className="text-[8px] text-white flex items-center justify-center h-full">🕐</span>
          </div>
        );
      case "post-only":
        return (
          <div className={cn(iconClasses, "bg-purple-500")} title={`${holiday.name} - Post-Market Only`}>
            <span className="text-[8px] text-white flex items-center justify-center h-full">📈</span>
          </div>
        );
      default:
        return null;
    }
  };

  const sizeConfig = size === "small" ? SMALL_CELL_SIZE : DEFAULT_CELL_SIZE;
  const smallExtraClasses = size === "small" ? `${SMALL_CELL_SIZE.dimensions} min-h-0` : "";

  const baseClasses = cn(
    "aspect-square w-full flex items-center justify-center text-center transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60 focus-visible:ring-offset-2 select-none",
    sizeConfig.font,
    sizeConfig.borderRadius,
    smallExtraClasses,
  );
  const isInteractive = Boolean(onClick && day.count > 0);
  const ariaLabel = day.isSelected ? `${tooltipText} (selected)` : tooltipText;

  if (!day.isCurrentMonth) {
    return <div className={cn(baseClasses, "text-muted-foreground/30 bg-transparent cursor-default")}>{day.label}</div>;
  }

  const intensityClass = getCellIntensityClass(day, maxCount);

  const buttonClasses = cn(
    baseClasses,
    intensityClass,
    day.isToday && "ring-2 ring-primary/30 ring-offset-1 font-semibold z-10",
    day.isSelected && "ring-2 ring-primary ring-offset-1 font-bold z-10",
    isInteractive ? "cursor-pointer hover:scale-110 hover:shadow-md hover:z-20" : "cursor-default",
  );

  const button = (
    <button
      type="button"
      onClick={handleClick}
      tabIndex={isInteractive ? 0 : -1}
      aria-label={ariaLabel}
      aria-current={day.isToday ? "date" : undefined}
      aria-disabled={!isInteractive}
      className={buttonClasses}
    >
      <div className="relative">
        {day.label}
        {day.isHoliday && holiday && getHolidayIcon()}
      </div>
    </button>
  );

  // Show tooltip if there's journal data OR if it's a holiday
  const shouldShowTooltip = (tooltipText && day.count > 0) || holiday;

  if (!shouldShowTooltip) {
    return button;
  }

  // Format holiday trading hours for tooltip
  const holidayTooltip = holiday ? formatHolidayTradingHours(holiday, t as any) : null;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs bg-popover text-popover-foreground border-border" hideArrow>
        {day.count > 0 && <p className="font-medium">{tooltipText}</p>}
        {holiday && (
          <div className="mt-2 space-y-1">
            <p className="text-xs font-semibold text-orange-600 dark:text-orange-400">{holiday.name}</p>
            {holidayTooltip && <p className="text-xs text-foreground whitespace-pre-line leading-tight">{holidayTooltip}</p>}
          </div>
        )}
      </TooltipContent>
    </Tooltip>
  );
});

CalendarCell.displayName = "CalendarCell";
